/// The Engine.IO v4 and Socket.IO v5 wire format, as strings, with no transport
/// and no state. Everything here is a pure function so the protocol can be
/// tested without a socket, which is the half most likely to be wrong.
///
/// We speak the subset `realtime.rs` actually uses and nothing else. The widget
/// never emits an event: it sends messages over the existing REST route and only
/// LISTENS here, which is what removes acknowledgements, binary packets and the
/// client half of the event encoder from this file.
///
/// Verified against the published protocol documents rather than inferred:
/// <https://socket.io/docs/v4/engine-io-protocol/> and
/// <https://socket.io/docs/v4/socket-io-protocol/>.

/// Engine.IO packet types. `4` (message) is the only one carrying a Socket.IO
/// packet; the rest are the transport's own.
const ENGINE_OPEN = '0';
const ENGINE_CLOSE = '1';
const ENGINE_PING = '2';
const ENGINE_PONG = '3';
const ENGINE_MESSAGE = '4';

/// Socket.IO packet types, which appear as the first character INSIDE an
/// Engine.IO message packet.
const SOCKET_CONNECT = '0';
const SOCKET_DISCONNECT = '1';
const SOCKET_EVENT = '2';
const SOCKET_CONNECT_ERROR = '4';

/// The handshake the widget presents. All three or nothing: `realtime.rs` falls
/// a partial set through to the JWT path and disconnects it there.
export interface SocketAuth {
  projectKey: string;
  conversationToken: string;
  conversationId: string;
}

export type Frame =
  /// The transport is up. `pingInterval` and `pingTimeout` are the server's, and
  /// the client is required to honour them rather than pick its own.
  | { kind: 'open'; pingInterval: number; pingTimeout: number }
  /// The server's heartbeat. Answer with `pong()` or it closes the connection.
  | { kind: 'ping' }
  /// The namespace accepted the auth payload. The widget is now in its room.
  | { kind: 'connected' }
  /// The namespace refused it. Distinct from a silent close: `realtime.rs`
  /// disconnects without a frame on a failed handshake deliberately, so this
  /// arrives only from the Socket.IO layer itself.
  | { kind: 'connectError'; message: string }
  | { kind: 'event'; name: string; payload: unknown }
  | { kind: 'disconnect' }
  /// Anything we do not model. Ignored rather than thrown: an unknown frame from
  /// a newer server must not take the widget down inside a customer's page.
  | { kind: 'other' };

/// `40` plus the auth payload, per the protocol document's own example
/// (`40{"token":"123"}`) for a CONNECT to the default namespace.
export function encodeConnect(auth: object): string {
  return `${ENGINE_MESSAGE}${SOCKET_CONNECT}${JSON.stringify(auth)}`;
}

export function pong(): string {
  return ENGINE_PONG;
}

/// Parses one frame. Never throws: a malformed body answers `other` and is
/// dropped, because this runs inside somebody else's page.
export function decode(frame: string): Frame {
  const engine = frame.charAt(0);
  const rest = frame.slice(1);

  if (engine === ENGINE_PING) return { kind: 'ping' };
  if (engine === ENGINE_CLOSE) return { kind: 'disconnect' };
  if (engine === ENGINE_OPEN) {
    const handshake = parse(rest) as { pingInterval?: unknown; pingTimeout?: unknown } | null;
    /* The defaults are the protocol's own published ones, used only when a
       server omits them. Guessing shorter would make us close a healthy
       connection; guessing longer would leave a dead one open. */
    return {
      kind: 'open',
      pingInterval: typeof handshake?.pingInterval === 'number' ? handshake.pingInterval : 25_000,
      pingTimeout: typeof handshake?.pingTimeout === 'number' ? handshake.pingTimeout : 20_000,
    };
  }
  if (engine !== ENGINE_MESSAGE) return { kind: 'other' };

  const socket = rest.charAt(0);
  const body = rest.slice(1);
  if (socket === SOCKET_CONNECT) return { kind: 'connected' };
  if (socket === SOCKET_DISCONNECT) return { kind: 'disconnect' };
  if (socket === SOCKET_CONNECT_ERROR) {
    const detail = parse(body) as { message?: unknown } | null;
    return { kind: 'connectError', message: typeof detail?.message === 'string' ? detail.message : 'refused' };
  }
  if (socket !== SOCKET_EVENT) return { kind: 'other' };

  /* An EVENT is `2["name",payload]`. A name that is not a string is not an
     event we can route, and an absent payload is legal. */
  const parsed = parse(body);
  if (!Array.isArray(parsed) || typeof parsed[0] !== 'string') return { kind: 'other' };
  return { kind: 'event', name: parsed[0], payload: parsed[1] };
}

function parse(body: string): unknown {
  try {
    return JSON.parse(body);
  } catch {
    return null;
  }
}
